/**
 * Society membership request states for the admin User List.
 *
 * The server owns the same state machine (apps/uninfo-api/src/config/societyMembership.js,
 * `classifyAdminMembershipState`). It is mirrored here because the admin list
 * classifies on every render and cannot call the API to do it; the API test
 * cross-checks the two so they cannot drift.
 *
 * Derived from the model, not invented:
 *  - `verified` — the admin approved it (`buildAdminApproveData`). A membership.
 *  - `rejected_reason` — an admin turned it down and the row was kept as the
 *    audit trail. Decided, and excluded from every admin queue.
 *  - `approved_by` on an unverified row — a society member countersigned it, which
 *    deliberately does NOT verify the request, so the admin still has to act.
 */

export type AdminMembershipState =
  | "approved"
  | "rejected"
  | "pending_countersigned"
  | "pending_unverified";

export interface StudentMembershipRequest {
  id: string;
  student_id: string;
  society_id: string;
  verified: boolean;
  verified_at: string | null;
  approved_by: string | null;
  rejected_reason: string | null;
  requested_at: string | null;
  society?: { id: string; name: string } | null;
}

export const classifyMembershipState = (
  membership: StudentMembershipRequest,
): AdminMembershipState => {
  if (membership.verified) return "approved";
  if (membership.rejected_reason) return "rejected";
  return membership.approved_by ? "pending_countersigned" : "pending_unverified";
};

/**
 * Whether an admin may still approve/reject this request. A decided row exposes
 * no actions: re-approving a rejected request would skip the student's own
 * re-apply, and re-approving a live membership is not a decision.
 */
export const isMembershipActionable = (membership: StudentMembershipRequest) =>
  classifyMembershipState(membership).startsWith("pending");